import React, {useMemo, useState} from 'react';

import cs from 'classnames';

import {
  DEFAULT_LOGO_URL,
  getLogoURL,
} from 'app/general/structures/SPFrame/SPHeader/CompanyLogo';
import {ColorPicker} from 'app/general/widgets/ColorPicker';
import StateButton from 'app/general/widgets/StateButton';
import {useAddTheme, useDeleteTheme, useUpdateTheme} from 'app/kbs/api';
import {useAccountSettingsState} from 'app/settings/api';
import {gt} from 'app/utils/i18n';

import {ThemeCard} from './ThemeCard';

import type {FetchBaseQueryError} from '@reduxjs/toolkit/query';
import type {KBThemeSerializedType} from 'sweet-types';

type Props = {
  theme: KBThemeSerializedType;
  kbName: string;
  onSubmit: (theme: KBThemeSerializedType | null) => unknown;
  onCancel: () => unknown;
};

export const EditThemeMeta = ({theme, onSubmit, onCancel, kbName}: Props) => {
  const [newTheme, setTheme] = useState<KBThemeSerializedType>(theme);

  const [updateTheme, {error: updateError}] = useUpdateTheme();
  const [addTheme, {error: addError}] = useAddTheme();
  const [deleteTheme] = useDeleteTheme();
  const settings = useAccountSettingsState().data;

  const {
    company_logo = DEFAULT_LOGO_URL,
    MEDIA_URL_READ = '',
    STATIC_URL = '/site_media/static/',
  } = settings == null ? {} : settings;

  const logo = useMemo(
    () => getLogoURL({company_logo, MEDIA_URL_READ, STATIC_URL}),
    [company_logo]
  );

  const handleColorChange = (colorName) => (colorValue) => {
    setTheme((prevTheme) => ({
      ...prevTheme,
      [colorName]: colorValue,
    }));
  };
  // Handle changes to the theme name
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setTheme((prevTheme) => ({
      ...prevTheme,
      name,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if the theme has a URL to decide between update and add
    if (newTheme.url) {
      // Update the theme
      const response = await updateTheme(newTheme).unwrap();
      onSubmit(response);
    } else {
      const response = await addTheme(newTheme).unwrap();
      onSubmit(response);
    }
  };

  const handleDelete = async (e) => {
    e.preventDefault();
    if (newTheme.url) {
      await deleteTheme(newTheme.url).unwrap();
    }
    onSubmit(null);
  };

  const handleCancel = (e) => {
    e.preventDefault();
    onCancel();
  };

  const error: Record<string, string[]> | undefined =
    (updateError && 'status' in updateError) ||
    (addError && 'status' in addError)
      ? ((updateError as FetchBaseQueryError)?.data as Record<
          string,
          string[]
        >) ??
        ((addError as FetchBaseQueryError)?.data as Record<string, string[]>)
      : undefined;

  return (
    <>
      <div className="row">
        <div className="col-8">
          <label>{gt('Theme Name:')}</label>
          <input
            type="text"
            value={newTheme.name}
            onChange={handleNameChange}
            className={cs('form-control mb-3', {
              'is-invalid': error?.name != null,
            })}
          />
          {error?.name != null ? (
            <div className="invalid-feedback">{error?.name}</div>
          ) : null}
          <div className="mb-4">
            <label>{gt('Navigation Background')}:</label>
            <p>{gt('Color used for the navigation bar background.')}</p>
            <ColorPicker
              initialColor={newTheme.nav_background}
              onSubmit={handleColorChange('nav_background')}
              error={error?.nav_background}
            />
          </div>

          <div className="mb-4">
            <label>{gt('Logo Background')}:</label>
            <p>{gt('Color used behind the logo.')}</p>
            <ColorPicker
              initialColor={newTheme.logo_background}
              onSubmit={handleColorChange('logo_background')}
              error={error?.logo_background}
            />
          </div>

          <div className="mb-4">
            <label>{gt('Navigation Text')}:</label>
            <p>{gt('Color used for the text in the navigation bar.')}</p>
            <ColorPicker
              initialColor={newTheme.nav_text}
              onSubmit={handleColorChange('nav_text')}
              error={error?.nav_text}
            />
          </div>

          <div className="mb-4">
            <label>{gt('Breadcrumb Text')}:</label>
            <p>{gt('Color used for breadcrumb text links.')}</p>
            <ColorPicker
              initialColor={newTheme.breadcrumb_text}
              onSubmit={handleColorChange('breadcrumb_text')}
              error={error?.breadcrumb_text}
            />
          </div>

          <div className="mb-4">
            <label>{gt('Accent Color')}:</label>
            <p>
              {gt('Color used for accent elements like buttons and links.')}
            </p>
            <ColorPicker
              initialColor={newTheme.accent_color}
              onSubmit={handleColorChange('accent_color')}
              error={error?.accent_color}
            />
          </div>

          <div className="mb-4">
            <label>{gt('Text Color')}:</label>
            <p>{gt('Primary color used for general text.')}</p>
            <ColorPicker
              initialColor={newTheme.text_color}
              onSubmit={handleColorChange('text_color')}
              error={error?.text_color}
            />
          </div>
        </div>

        <div className="col-4">
          <ThemeCard
            theme={newTheme}
            kbName={kbName}
            logo={logo}
            className={'sticky-top'}
          />
        </div>
      </div>

      <div className="row">
        {newTheme.url ? (
          <StateButton
            className="btn btn-link text-danger float-right"
            busyText={gt('Deleting...')}
            onClick={handleDelete}
          >
            {gt('Delete Theme')}
          </StateButton>
        ) : null}
        <button className="btn btn-link text-secondary" onClick={handleCancel}>
          {gt('Cancel')}
        </button>
        <StateButton
          type="submit"
          className="btn btn-primary"
          busyText={gt('Saving...')}
          onClick={handleSubmit}
        >
          {gt('Save Theme')}
        </StateButton>
      </div>
    </>
  );
};
